import type React from "react";
import {observer} from "mobx-react-lite";
import {useMemo, useState} from "react";
import {Autocomplete, Checkbox, CircularProgress, Skeleton, Stack, TextField, Typography} from "@mui/material";
import {PersonOutline} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag";
import {authStore, notificationStore} from "~/stores/init";
import {
    SUPPLIER_CONTACT_SEARCH_PLACEHOLDER,
    SUPPLIER_CONTACT_TYPE_LABEL,
} from "~/pages/supplier-360/constants/supplierEntityLabels";
import {useUserSearchQuery} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useUserSearchQuery";
import {useDebouncedSearch} from "~/hooks/useDebouncedSearch";
import {
    useAddSupplierContactMutation,
    useRemoveSupplierContactMutation,
} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierContactMutations";
import {
    type SupplierContact,
    type SupplierContactType,
    type SupplierContactUser,
    supplierContactUserName,
} from "~/pages/supplier-360/types/Supplier360Types";

const MAX_VISIBLE_TAGS = 2;
const USER_OPTION_COUNT = 20;

type SupplierContactsFieldProps = {
    entityId: number;
    contactType: SupplierContactType;
    /**
     * This entity's assignments of this type — the field's selection, straight from the backend:
     * a row's existence *is* the checkmark (§2.23). Checking writes one, unchecking deletes it.
     */
    contacts: SupplierContact[];
    isLoading: boolean;
};

export const SupplierContactsField: React.FC<SupplierContactsFieldProps> = observer(({
                                                                                         entityId,
                                                                                         contactType,
                                                                                         contacts,
                                                                                         isLoading,
                                                                                     }) => {
    const label = SUPPLIER_CONTACT_TYPE_LABEL[contactType];
    // Debounced: one request per pause in typing, not per keystroke — same as the Parent picker.
    const {input: inputValue, debounced: search, setInput: setInputValue} = useDebouncedSearch();
    const [isOpen, setIsOpen] = useState(false);
    const canEdit = authStore.hasEditPermission;

    const {mutate: addContact, isPending: isAdding} = useAddSupplierContactMutation();
    const {mutate: removeContact, isPending: isRemoving} = useRemoveSupplierContactMutation();

    // The candidate pool — real Mithra accounts, searched only while the dropdown is open. Picking
    // one is the only way to add someone here; there is no "add a new person" path any more (§2.23,
    // re-modeled 2026-10-23 per the user — see `SupplierContact`'s own docstring).
    const {data: pool, isFetching} = useUserSearchQuery(search, USER_OPTION_COUNT, isOpen);

    /** The assignment behind each checked user — what unchecking has to `DELETE`. */
    const assignmentByUserId = useMemo(
        () => new Map(contacts.map(contact => [contact.user.id, contact])),
        [contacts],
    );

    const selected = useMemo(() => contacts.map(contact => contact.user), [contacts]);

    /**
     * The search runs server-side, so its page is shown as-is. With no search term the checked
     * people head the list — a field that opens on its own selection reads as a selection, and
     * they would otherwise be buried somewhere in the pool.
     */
    const options = useMemo<SupplierContactUser[]>(() => {
        const candidates = pool ?? [];
        if (search) return candidates;

        const byId = new Map<number, SupplierContactUser>(selected.map(u => [u.id, u]));
        candidates.forEach(user => {
            if (!byId.has(user.id)) byId.set(user.id, user);
        });
        return [...byId.values()];
    }, [pool, search, selected]);

    const notifyError = (message: string) => notificationStore.show(message, {
        severity: 'error',
        id: `supplier-contact-error-${entityId}-${contactType}`,
        autoHideDuration: 3000,
    });

    /** Selects someone who already exists — the id is all the endpoint needs (§2.23). */
    const attachUser = (user: SupplierContactUser) => {
        if (assignmentByUserId.has(user.id)) return;
        const displayName = supplierContactUserName(user);
        addContact(
            {entityId, contact_type: contactType, user_id: user.id, displayName},
            {onError: () => notifyError(`Could not select ${displayName}. Please try again.`)},
        );
    };

    /** Unselects one person: deletes the assignment only, never the person (§2.23). */
    const detach = (contact: SupplierContact) => {
        const displayName = supplierContactUserName(contact.user);
        removeContact(
            {entityId, contactId: contact.id, displayName},
            {onError: () => notifyError(`Could not unselect ${displayName}. Please try again.`)},
        );
    };

    /**
     * MUI hands back the whole new selection, so the change is read as a diff against the backend's
     * rows: what appeared is checked (`POST`), what disappeared is unchecked (`DELETE`). That
     * covers the checkbox and the tag's delete button alike.
     */
    const onChange = (values: SupplierContactUser[]) => {
        const nextIds = new Set(values.map(user => user.id));

        values.filter(user => !assignmentByUserId.has(user.id)).forEach(attachUser);
        contacts.filter(contact => !nextIds.has(contact.user.id)).forEach(detach);
    };

    return (
        <Stack sx={{minWidth: 0}}>
            {isLoading
                ? <Skeleton variant="rounded" width="100%" height={40}/>
                : <Autocomplete<SupplierContactUser, true, true, false>
                multiple
                disableClearable
                // Read-only accounts see who is assigned, but can't open the list or remove anyone.
                readOnly={!canEdit}
                disableCloseOnSelect
                size="small"
                open={isOpen}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                loading={isFetching}
                options={options}
                value={selected}
                inputValue={inputValue}
                onInputChange={(_event, value, reason) => {
                    if (reason === 'input') setInputValue(value);
                    if (reason === 'clear') setInputValue('');
                }}
                onChange={(_event, values) => onChange(values)}
                getOptionLabel={supplierContactUserName}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // Searching is the server's job — the response comes back already narrowed.
                filterOptions={opts => opts}
                noOptionsText={isFetching ? 'Searching…' : 'No match'}
                renderValue={(values, getItemProps) => {
                    const hidden = values.slice(MAX_VISIBLE_TAGS).map(supplierContactUserName);

                    return (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{maxWidth: '100%'}}>
                            {values.slice(0, MAX_VISIBLE_TAGS).map((value, index) => {
                                // `onDelete` goes through the same diff — it unchecks the
                                // person for this supplier, it does not delete the person.
                                const {key, ...itemProps} = getItemProps({index});
                                return (
                                    <Tag
                                        key={key}
                                        {...itemProps}
                                        label={supplierContactUserName(value)}
                                        size="small"
                                        variant="outlined"
                                        color="default"
                                    />
                                );
                            })}
                            {hidden.length > 0 && (
                                <Tag
                                    label={`+${hidden.length} more`}
                                    title={hidden.join(', ')}
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                />
                            )}
                        </Stack>
                    );
                }}
                renderOption={({key, ...optionProps}, option, {selected: isSelected}) => (
                    <li key={key} {...optionProps}>
                        <Checkbox size="small" checked={isSelected} disableRipple sx={{py: 0}}/>
                        <PersonOutline fontSize="small" color="action" sx={{mr: 1, flexShrink: 0}}/>
                        <Stack direction="row" gap={0.5} sx={{minWidth: 0}}>
                            <Typography title={supplierContactUserName(option)} sx={{minWidth: 0}} variant="body2" component="span" display="block" noWrap>{supplierContactUserName(option)}</Typography>
                            <Typography
                                sx={{minWidth: 0}}
                                color="grey.500"
                                variant="caption"
                                component="span"
                                display="block"
                                noWrap
                                title={option.email}
                            >
                                {option.email}
                            </Typography>
                        </Stack>
                    </li>
                )}
                renderInput={params => <TextField
                    {...params}
                    label={label}
                    placeholder={canEdit ? SUPPLIER_CONTACT_SEARCH_PLACEHOLDER[contactType] : undefined}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {(isAdding || isRemoving) && <CircularProgress color="inherit" size={16}/>}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />}
                />}
        </Stack>
    );
});
